// src/pages/CouponWallet.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import FullBleed from '../components/FullBleed';
import StageFrame from '../components/StageFrame';
import CouponQrBlock from '../components/CouponQrBlock';
import bg from '../assets/card.png';
import { couponClaimStyles } from '../utils/couponClaimStyles';
import { CouponLangSwitch, CouponLocaleProvider, useCouponLocale } from '../context/CouponLocaleContext';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';
import { resolveCouponName } from '../utils/couponContent';

axios.defaults.withCredentials = true;

function CouponWalletBody() {
  const { t, lang } = useCouponLocale();
  const token = useMemo(() => {
    const usp = new URLSearchParams(window.location.search);
    return (usp.get('token') || '').trim();
  }, []);

  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const STATUS_LABEL = useMemo(() => ({
    unused: { text: t('statusUnused'), color: '#166534', bg: '#dcfce7' },
    used: { text: t('statusUsed'), color: '#6b7280', bg: '#f3f4f6' },
    expired: { text: t('statusExpired'), color: '#b45309', bg: '#fef3c7' },
  }), [t]);

  useEffect(() => {
    if (!token) {
      setError(t('walletMissing'));
      setLoading(false);
      return;
    }
    axios.get('/api/coupon/claim-by-token', { params: { token } })
      .then(({ data }) => {
        if (data?.ok && data?.claim) setClaim(data.claim);
        else setError(t('walletNotFound'));
      })
      .catch((err) => {
        setError(err?.response?.data?.error === 'NOT_FOUND' ? t('walletNotFound') : t('walletLoadFail'));
      })
      .finally(() => setLoading(false));
  }, [token, t]);

  const statusInfo = claim ? (STATUS_LABEL[claim.status] || STATUS_LABEL.unused) : null;
  const displayCode = claim ? resolveDisplayCode(claim.claimId, claim.displayCode) : '';
  const displayName = claim ? resolveCouponName(claim, lang) : '';

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px, scale, w }) => {
          if (!w || !scale) return null;
          return (
            <>
              <style>{couponClaimStyles(px)}</style>
              <div className="cc-box" style={{ maxHeight: 'none' }}>
                <CouponLangSwitch px={px} />
                <h1 className="cc-title">{t('walletTitle')}</h1>
                {loading && <p className="cc-sub">{t('loading')}</p>}
                {error && <div className="cc-msg err">{error}</div>}
                {!loading && !error && claim && (
                  <>
                    <div className="cc-coupon-card">
                      <div className="cc-coupon-name">{displayName}</div>
                      <div className="cc-coupon-desc">{t('expireLabel')} {claim.endAt || '—'}</div>
                    </div>
                    <div
                      style={{
                        display: 'inline-block',
                        padding: `${px(10)}px ${px(24)}px`,
                        borderRadius: `${px(12)}px`,
                        fontSize: `${px(38)}px`,
                        fontWeight: 800,
                        marginBottom: `${px(20)}px`,
                        color: statusInfo.color,
                        background: statusInfo.bg,
                      }}
                    >
                      {statusInfo.text}
                    </div>
                    {claim.status === 'unused' && (
                      <>
                        <CouponQrBlock
                          redeemToken={claim.redeemToken}
                          claimId={claim.claimId}
                          displayCode={displayCode}
                          px={px}
                          size={Math.round(px(400))}
                          label={t('redeemCode')}
                        />
                        <p className="cc-note">{t('qrNote')}</p>
                      </>
                    )}
                    {claim.status === 'used' && (
                      <p className="cc-note">{t('walletUsedNote')}</p>
                    )}
                    {claim.status === 'expired' && (
                      <p className="cc-note">{t('walletExpiredNote')}</p>
                    )}
                  </>
                )}
              </div>
            </>
          );
        }}
      />
    </FullBleed>
  );
}

export default function CouponWallet() {
  return (
    <CouponLocaleProvider>
      <CouponWalletBody />
    </CouponLocaleProvider>
  );
}
